import { useEffect, useRef, type RefObject } from 'react';

/** Stack of open dialogs/drawers: Escape only closes the topmost one. */
const layerStack: HTMLElement[] = [];
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
export const focusables = (el: HTMLElement) => [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((x) => x.offsetParent !== null || x === document.activeElement);

export function useLayer(ref: RefObject<HTMLElement | null>, onClose: () => void, opts: { trap: boolean; initialFocus: () => HTMLElement | null }) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const initialRef = useRef(opts.initialFocus);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const previous = document.activeElement as HTMLElement | null;
    layerStack.push(el);
    if (opts.trap) document.body.classList.add('no-scroll');
    (initialRef.current() ?? el).focus();
    const onKey = (e: KeyboardEvent) => {
      if (layerStack.at(-1) !== el) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeRef.current();
      } else if (e.key === 'Tab' && opts.trap) {
        const items = focusables(el);
        if (!items.length) return e.preventDefault();
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (e.shiftKey && (document.activeElement === first || !el.contains(document.activeElement))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      layerStack.splice(layerStack.indexOf(el), 1);
      if (!layerStack.some((x) => x.classList.contains('modal'))) document.body.classList.remove('no-scroll');
      if (previous && document.contains(previous)) previous.focus();
    };
    // mount/unmount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
